import React, { memo, useState, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import type { Question } from '../../types';
import { QuestionAnimationPlayer } from './QuestionAnimationPlayer';
import { QuestionImageOrVideo } from './QuestionImageOrVideo';

interface YesNoQuestionProps {
  question: Question;
  questionNumber?: number;
  selectedAnswer?: string;
  isAnswered?: boolean;
  onAnswer?: (answerId: string) => void;
  streakMessage?: string | null;
}

/**
 * YesNoQuestion — Binary decision format
 *
 * Used for questions that are genuinely YES or NO decisions.
 * Shows question + scenario (animation/image if any) + two large tap buttons.
 * Maps YES/NO to existing option IDs via question.yesNoMapping.
 */
export const YesNoQuestion: React.FC<YesNoQuestionProps> = memo(({
  question,
  questionNumber = 1,
  selectedAnswer,
  isAnswered,
  onAnswer,
  streakMessage,
}) => {
  const { t_nested } = useLanguage();
  const [lastClickTime, setLastClickTime] = useState(0);

  const handleClick = useCallback((optionId: string) => {
    if (isAnswered) return;
    const now = Date.now();
    if (now - lastClickTime < 200) return;
    setLastClickTime(now);
    onAnswer?.(optionId);
  }, [isAnswered, lastClickTime, onAnswer]);

  const yesId = question.yesNoMapping?.yes ?? '';
  const noId = question.yesNoMapping?.no ?? '';
  const correctId = question.correctAnswerId || question.answerId;
  const questionText = question.text || question.question;

  const hasAnimation = !!(question as { animationId?: string | null }).animationId;
  const hasImage = !!(question as { imageUrl?: string }).imageUrl;

  const getButtonState = (optionId: string) => {
    if (!isAnswered) {
      return selectedAnswer === optionId ? 'selected' : 'idle';
    }
    if (optionId === correctId) return 'correct';
    if (selectedAnswer === optionId && optionId !== correctId) return 'wrong';
    return 'dimmed';
  };

  const yesState = getButtonState(yesId);
  const noState = getButtonState(noId);

  return (
    <div className="yesno-question">
      {/* Animation or image */}
      {hasAnimation && (
        <QuestionAnimationPlayer
          animationId={(question as { animationId?: string | null }).animationId}
        />
      )}
      {hasImage && !hasAnimation && question.imageUrl && (
        <div className="yesno-image-container">
          <QuestionImageOrVideo
            url={question.imageUrl}
            alt="question visual"
            className="yesno-image"
          />
        </div>
      )}

      {/* Question text */}
      <div className="yesno-question-text">
        <span className="professional-question-number">{questionNumber})</span>{' '}
        {questionText}
      </div>

      {/* YES / NO buttons */}
      <div className="yesno-buttons">
        <button
          type="button"
          className={`yesno-btn yesno-btn--yes yesno-btn--${yesState}`}
          onClick={() => handleClick(yesId)}
          disabled={isAnswered}
          aria-pressed={selectedAnswer === yesId}
          aria-label={`${t_nested('ui.yes')} (A)`}
        >
          <span className="yesno-option-letter">A</span>
          {yesState === 'correct' && <span className="yesno-icon">✓</span>}
          {yesState === 'wrong' && <span className="yesno-icon">✗</span>}
          <span className="yesno-label">{t_nested('ui.yes')}</span>
        </button>

        <button
          type="button"
          className={`yesno-btn yesno-btn--no yesno-btn--${noState}`}
          onClick={() => handleClick(noId)}
          disabled={isAnswered}
          aria-pressed={selectedAnswer === noId}
          aria-label={`${t_nested('ui.no')} (B)`}
        >
          <span className="yesno-option-letter">B</span>
          {noState === 'correct' && <span className="yesno-icon">✓</span>}
          {noState === 'wrong' && <span className="yesno-icon">✗</span>}
          <span className="yesno-label">{t_nested('ui.no')}</span>
        </button>
      </div>

      {streakMessage && (
        <div className="practice-streak-message">{streakMessage}</div>
      )}
    </div>
  );
});

YesNoQuestion.displayName = 'YesNoQuestion';
